import { useEffect, useMemo, useState } from 'react';
import { Layers, Truck } from 'lucide-react';

/**
 * SetupGroupGanttRibbon — horizontal Gantt-style timeline for a Setup Group.
 *
 * Props:
 *   api        — axios instance
 *   groupId    — setup_group_id to fetch
 *   onJobClick — (job) => void, invoked when a segment is clicked (1-click = open drawer)
 *   statusOf   — optional (job) => "not_allocated" | "partially_allocated" | "allocated" | "confirmed" | "docketed" | "invoiced" | "mixed"
 *   compact    — if true, renders a tighter single-row timeline suited for inline Schedule rows
 *   phase      — 'all' | 'mob' | 'demob' — filters which job buckets render. Defaults to 'all'.
 *                'mob' shows parent + mobilisation legs (Day 1 view).
 *                'demob' shows parent + demobilisation legs (Last Day view).
 *
 * Visual: 1-hour ticks, status-coloured segments (Red=Not Allocated, Amber=Partial, Green=Confirmed).
 */
const SetupGroupGanttRibbon = ({ api, groupId, onJobClick, statusOf, compact = false, phase = 'all' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function run() {
      if (!groupId) return;
      setLoading(true);
      try {
        const res = await api.get(`/setup-groups/${groupId}`);
        if (!ignore) setData(res.data);
      } catch (err) {
        console.error('SetupGroupGanttRibbon: failed to fetch group', err);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [groupId, api]);

  const jobs = useMemo(() => {
    if (!data) return [];
    const parent = data.parent_job ? [{ ...data.parent_job, _bucket: 'primary' }] : [];
    const mobs = (data.mobilisation_jobs || [])
      .filter(j => !j.is_setup_parent)
      .map(j => ({ ...j, _bucket: 'mob' }));
    const demobs = (data.demobilisation_jobs || []).map(j => ({ ...j, _bucket: 'demob' }));
    if (phase === 'mob') return [...parent, ...mobs];
    if (phase === 'demob') return [...parent, ...demobs];
    return [...parent, ...mobs, ...demobs];
  }, [data, phase]);

  const timeline = useMemo(() => computeTimeline(jobs), [jobs]);

  if (!groupId) return null;
  if (loading && !data) {
    return <div className={`text-xs text-muted-foreground px-3 py-2 ${compact ? '' : 'border-t'}`}>Loading timeline…</div>;
  }
  if (!data || jobs.length === 0) return null;

  const { startMin, endMin, totalMin, hourTicks } = timeline;

  return (
    <div
      className={`relative bg-muted/20 ${compact ? 'py-2' : 'px-3 pt-2 pb-3 border-t'}`}
      data-testid={`gantt-ribbon-${groupId}`}
    >
      {!compact && (
        <div className="flex items-center gap-2 mb-2">
          <Layers className="h-3.5 w-3.5 text-blue-500" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {phase === 'mob' ? 'Mobilisation Timeline' : phase === 'demob' ? 'Demobilisation Timeline' : 'Setup Group Timeline'}
          </span>
          {data.profile_name && (
            <span className="text-xs text-muted-foreground">· {data.profile_name}</span>
          )}
          <span className="text-xs text-muted-foreground ml-auto">
            {minutesToHHMM(startMin)}—{minutesToHHMM(endMin)}
          </span>
        </div>
      )}

      {/* Hour tick header */}
      <div className="relative h-4 mb-1">
        {hourTicks.map(t => {
          const left = ((t - startMin) / totalMin) * 100;
          return (
            <div
              key={t}
              className="absolute top-0 text-[10px] text-muted-foreground -translate-x-1/2"
              style={{ left: `${left}%` }}
            >
              {minutesToHHMM(t)}
            </div>
          );
        })}
      </div>

      {/* Grid */}
      <div
        className="relative rounded border bg-background/60"
        style={{ height: compact ? 28 : 32 }}
      >
        {/* Hour grid lines */}
        {hourTicks.map(t => {
          const left = ((t - startMin) / totalMin) * 100;
          return (
            <div
              key={`line-${t}`}
              className="absolute top-0 bottom-0 border-l border-dashed border-muted-foreground/20"
              style={{ left: `${left}%` }}
            />
          );
        })}

        {/* Segments */}
        {jobs.map((job, idx) => {
          const { left, width } = segmentGeometry(job, startMin, totalMin);
          const status = statusOf ? statusOf(job) : (job.status || 'not_allocated');
          const { bg, border, text } = statusColors(status, job._bucket);
          const isTransport = !!job.transport_type;
          const label = `${job.equipment_type}${isTransport ? ' · Transport' : ''}`;
          const time = minutesToHHMM(parseTime(job.start_time));
          return (
            <button
              key={job.id || idx}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onJobClick?.(job);
              }}
              title={`${label} · ${time} · ${prettyStatus(status)} · #${job.job_number || ''}`}
              className={`absolute top-0 bottom-0 ${bg} ${border} ${text} border rounded-sm px-1.5 text-[10px] font-medium truncate flex items-center gap-1 hover:ring-2 hover:ring-offset-1 hover:ring-primary/40 transition`}
              style={{ left: `${left}%`, width: `max(${width}%, 40px)` }}
              data-testid={`gantt-segment-${job.id}`}
            >
              {isTransport && <Truck className="h-3 w-3 shrink-0" />}
              <span className="truncate">{label}</span>
              <span className="opacity-80 shrink-0">{time}</span>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      {!compact && (
        <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
          <LegendSwatch className="bg-red-500/20 border-red-500/60" /> Not Allocated
          <LegendSwatch className="bg-amber-500/20 border-amber-500/60" /> Partial
          <LegendSwatch className="bg-emerald-500/20 border-emerald-500/60" /> Confirmed
          <LegendSwatch className="bg-blue-500/20 border-blue-500/60" /> Primary
        </div>
      )}
    </div>
  );
};

// ----------- helpers -----------

function parseTime(hhmm) {
  if (!hhmm) return 0;
  const [h, m] = String(hhmm).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function minutesToHHMM(total) {
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

function prettyStatus(s) {
  if (!s) return 'Unknown';
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Default segment duration when a job has no end_time — fall back to 1h block. */
const DEFAULT_DURATION_MIN = 60;

function jobDurationMin(job) {
  const start = parseTime(job.start_time);
  if (job.end_time) {
    const end = parseTime(job.end_time);
    if (end > start) return end - start;
  }
  if (job.est_duration) return Math.max(30, Math.round(Number(job.est_duration) * 60));
  return DEFAULT_DURATION_MIN;
}

function segmentGeometry(job, startMin, totalMin) {
  const jobStart = parseTime(job.start_time);
  const dur = jobDurationMin(job);
  const left = Math.max(0, ((jobStart - startMin) / totalMin) * 100);
  const width = Math.max(2, (dur / totalMin) * 100);
  return { left, width };
}

/** Returns { startMin, endMin, totalMin, hourTicks[] } for the whole group. */
function computeTimeline(jobs) {
  if (!jobs || jobs.length === 0) {
    return { startMin: 0, endMin: 60, totalMin: 60, hourTicks: [] };
  }
  let minStart = Infinity;
  let maxEnd = -Infinity;
  for (const j of jobs) {
    const s = parseTime(j.start_time);
    const e = s + jobDurationMin(j);
    if (s < minStart) minStart = s;
    if (e > maxEnd) maxEnd = e;
  }
  // Pad 30 minutes on each side for visual breathing room
  const padded = {
    start: Math.max(0, minStart - 30),
    end: Math.min(24 * 60, maxEnd + 30),
  };
  const total = Math.max(60, padded.end - padded.start);
  // Hour ticks — snap to next whole hour >= start
  const ticks = [];
  const firstTick = Math.ceil(padded.start / 60) * 60;
  for (let t = firstTick; t <= padded.end; t += 60) ticks.push(t);
  return { startMin: padded.start, endMin: padded.end, totalMin: total, hourTicks: ticks };
}

function statusColors(status, bucket) {
  // Primary row always blue (visual anchor)
  if (bucket === 'primary') {
    return {
      bg: 'bg-blue-500/20',
      border: 'border-blue-500/60',
      text: 'text-blue-900 dark:text-blue-100',
    };
  }
  switch (status) {
    case 'confirmed':
    case 'docketed':
    case 'invoiced':
      return { bg: 'bg-emerald-500/20', border: 'border-emerald-500/60', text: 'text-emerald-900 dark:text-emerald-100' };
    case 'allocated':
    case 'partially_allocated':
    case 'mixed':
      return { bg: 'bg-amber-500/20', border: 'border-amber-500/60', text: 'text-amber-900 dark:text-amber-100' };
    case 'not_allocated':
    default:
      return { bg: 'bg-red-500/15', border: 'border-red-500/60', text: 'text-red-900 dark:text-red-100' };
  }
}

const LegendSwatch = ({ className }) => (
  <span className={`inline-block w-3 h-3 rounded-sm border ${className}`} />
);

export default SetupGroupGanttRibbon;
